import { useEffect, useState, type FormEvent } from 'react'
import type { SecurityResponse, SessionResponse, TokenDescription, TotpSetup } from '../shared/types'
import { api, ApiRequestError } from './api'
import { Card, Pill, Spinner } from './components'

/**
 * Everything that happens before or around a session: signing in (one step,
 * or two when the account has a second factor), forgotten passwords, the
 * invitation and reset links, enrolling an authenticator, and the account's
 * own security page.
 */

function message(err: unknown, fallback: string): string {
  return err instanceof ApiRequestError ? err.message : fallback
}

// --- Sign in -------------------------------------------------------------------

export function Login({
  session,
  onChange,
}: {
  session: SessionResponse | null
  onChange: () => void
}) {
  const [mode, setMode] = useState<'password' | 'forgot' | 'forgot-sent'>('password')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [mailEnabled, setMailEnabled] = useState<boolean>(session?.mailEnabled ?? false)

  if (session?.twoFactorPending) {
    return <SecondFactor method={session.twoFactorPending} onChange={onChange} />
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      if (mode === 'forgot') {
        const r = await api.forgotPassword(username)
        setMailEnabled(r.mailEnabled)
        setMode('forgot-sent')
        setBusy(false)
        return
      }
      await api.login(password, username)
      onChange()
    } catch (err) {
      setError(message(err, 'Sign-in failed'))
      setBusy(false)
    }
  }

  if (mode === 'forgot-sent') {
    return (
      <main>
        <div className="login card">
          <h2>Check your email</h2>
          <div className="body">
            {mailEnabled ? (
              <p className="note" style={{ marginTop: 0 }}>
                If <code>{username}</code> is an account with an email address, a reset link is on
                its way. It works once and expires in 30 minutes.
              </p>
            ) : (
              <p className="note" style={{ marginTop: 0 }}>
                This service cannot send email yet, so ask an admin to send you a reset link.
              </p>
            )}
            <button type="button" onClick={() => setMode('password')}>Back to sign in</button>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main>
      <form className="login card" onSubmit={(e) => void submit(e)}>
        <img className="login-logo" src="/brand/clinic51-logo.png" alt="Clinic51" style={{ marginTop: 20 }} />
        <h2>{mode === 'forgot' ? 'Forgotten password' : 'Results Portal'}</h2>
        <div className="body">
          {mode === 'forgot' && (
            <p className="note" style={{ marginTop: 0 }}>
              Enter your username or email and we will send a link to choose a new password.
            </p>
          )}
          <label htmlFor="username">Username</label>
          <input
            id="username"
            type="text"
            value={username}
            autoComplete="username"
            placeholder={mode === 'forgot' ? 'you@clinic51.co.uk' : 'leave blank for the shared password'}
            autoFocus={mode === 'forgot'}
            onChange={(e) => setUsername(e.target.value)}
          />
          {mode === 'password' && (
            <>
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                autoFocus
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
              />
            </>
          )}
          <button type="submit" disabled={busy || (mode === 'password' ? password === '' : username.trim() === '')}>
            {busy ? 'Working…' : mode === 'forgot' ? 'Send reset link' : 'Sign in'}
          </button>
          {error !== null && <p className="error">{error}</p>}
          <p className="note">
            {mode === 'forgot' ? (
              <button type="button" className="link-button" onClick={() => { setMode('password'); setError(null) }}>
                Back to sign in
              </button>
            ) : (
              <button type="button" className="link-button" onClick={() => { setMode('forgot'); setError(null) }}>
                Forgotten your password?
              </button>
            )}
          </p>
        </div>
      </form>
    </main>
  )
}

/** The second step: a code from the authenticator app or from the email just sent. */
function SecondFactor({ method, onChange }: { method: 'email' | 'totp'; onChange: () => void }) {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.twoFactor(code)
      onChange()
    } catch (err) {
      setError(message(err, 'That code was not accepted'))
      setBusy(false)
      if (err instanceof ApiRequestError && err.code === 'too_many_attempts') onChange()
    }
  }

  async function resend() {
    setNotice(null)
    try {
      await api.resendCode()
      setNotice('A new code has been sent.')
    } catch (err) {
      setError(message(err, 'Could not resend the code'))
    }
  }

  return (
    <main>
      <form className="login card" onSubmit={(e) => void submit(e)}>
        <h2>{method === 'totp' ? 'Authenticator code' : 'Check your email'}</h2>
        <div className="body">
          <p className="note" style={{ marginTop: 0 }}>
            {method === 'totp'
              ? 'Enter the six-digit code from your authenticator app, or one of your recovery codes.'
              : 'A six-digit code has been emailed to you. It expires in 10 minutes.'}
          </p>
          <label htmlFor="code">Code</label>
          <input
            id="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            autoFocus
            onChange={(e) => setCode(e.target.value)}
          />
          <button type="submit" disabled={busy || code.trim() === ''}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
          {error !== null && <p className="error">{error}</p>}
          {notice !== null && <p className="note">{notice}</p>}
          <p className="note">
            {method === 'email' && (
              <>
                <button type="button" className="link-button" onClick={() => void resend()}>Send a new code</button>
                {' · '}
              </>
            )}
            <button type="button" className="link-button" onClick={() => void api.logout().then(onChange)}>
              Start again
            </button>
          </p>
        </div>
      </form>
    </main>
  )
}

// --- Invitation and reset links ---------------------------------------------------

/** Reached from `#invite=…` or `#reset=…`, signed out. Sets a password, then hands over to sign-in. */
export function SetPasswordFromLink({
  kind,
  token,
  onDone,
}: {
  kind: 'invite' | 'reset'
  token: string
  onDone: () => void
}) {
  const [desc, setDesc] = useState<TokenDescription | null>(null)
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    api.describeToken(kind, token).then(setDesc).catch(() => setDesc({ valid: false, kind, username: null }))
  }, [kind, token])

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (next !== again) return setError('The two passwords differ')
    setBusy(true)
    setError(null)
    try {
      await api.acceptToken(kind, token, next)
      setDone(true)
    } catch (err) {
      setError(message(err, 'Could not set the password'))
      setBusy(false)
    }
  }

  if (desc === null) return <Spinner />

  if (done) {
    return (
      <main>
        <div className="login card">
          <h2>Password set</h2>
          <div className="body">
            <p className="note" style={{ marginTop: 0 }}>
              Sign in as <code>{desc.username}</code> with your new password.
            </p>
            <button type="button" onClick={onDone}>Sign in</button>
          </div>
        </div>
      </main>
    )
  }

  if (!desc.valid) {
    return (
      <main>
        <div className="login card">
          <h2>This link has expired</h2>
          <div className="body">
            <p className="note" style={{ marginTop: 0 }}>
              {kind === 'invite'
                ? 'Invitation links work once and last 72 hours. Ask an admin to send a new one.'
                : 'Reset links work once and last 30 minutes. Request another from the sign-in page.'}
            </p>
            <button type="button" onClick={onDone}>Go to sign in</button>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main>
      <form className="login card" onSubmit={(e) => void submit(e)}>
        <h2>{kind === 'invite' ? 'Welcome — choose a password' : 'Choose a new password'}</h2>
        <div className="body">
          <p className="note" style={{ marginTop: 0 }}>
            For <code>{desc.username}</code>. At least 12 characters; nobody else will know it.
          </p>
          <label htmlFor="next">Password</label>
          <input id="next" type="password" value={next} autoFocus autoComplete="new-password" onChange={(e) => setNext(e.target.value)} />
          <label htmlFor="again">Password again</label>
          <input id="again" type="password" value={again} autoComplete="new-password" onChange={(e) => setAgain(e.target.value)} />
          <button type="submit" disabled={busy || next.length < 12}>
            {busy ? 'Saving…' : 'Set password'}
          </button>
          {error !== null && <p className="error">{error}</p>}
        </div>
      </form>
    </main>
  )
}

// --- Forced steps after sign-in ------------------------------------------------------

/** Forced on an account still on a one-time password. Nothing else renders until it is done. */
export function ChangePassword({ username, onDone }: { username: string; onDone: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (next !== again) return setError('The two new passwords differ')
    setBusy(true)
    setError(null)
    try {
      await api.changePassword(current, next)
      onDone()
    } catch (err) {
      setError(message(err, 'Could not change the password'))
      setBusy(false)
    }
  }

  return (
    <main>
      <form className="login card" onSubmit={(e) => void submit(e)}>
        <h2>Choose your password</h2>
        <div className="body">
          <p className="note" style={{ marginTop: 0 }}>
            Signed in as <code>{username}</code> with a one-time password. Replace it with one
            only you know — at least 12 characters.
          </p>
          <label htmlFor="current">One-time password</label>
          <input id="current" type="password" value={current} autoFocus autoComplete="current-password" onChange={(e) => setCurrent(e.target.value)} />
          <label htmlFor="next">New password</label>
          <input id="next" type="password" value={next} autoComplete="new-password" onChange={(e) => setNext(e.target.value)} />
          <label htmlFor="again">New password again</label>
          <input id="again" type="password" value={again} autoComplete="new-password" onChange={(e) => setAgain(e.target.value)} />
          <button type="submit" disabled={busy || next.length < 12 || current === ''}>
            {busy ? 'Saving…' : 'Save and continue'}
          </button>
          {error !== null && <p className="error">{error}</p>}
        </div>
      </form>
    </main>
  )
}

/**
 * Enrol an authenticator app: scan the QR code or type the key, then prove
 * it with one code. Used both as the forced step for an account that must
 * have one and from the security page for anyone who wants one.
 */
export function EnrolTotp({ forced, onDone }: { forced: boolean; onDone: () => void }) {
  const [setup, setSetup] = useState<TotpSetup | null>(null)
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState<string[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    api.totpSetup()
      .then((r) => { if (live) setSetup(r) })
      .catch((e: unknown) => { if (live) setError(message(e, 'Could not start set-up')) })
    return () => { live = false }
  }, [])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      setCodes((await api.totpConfirm(code)).codes)
    } catch (err) {
      setError(message(err, 'That code was not accepted'))
    } finally {
      setBusy(false)
    }
  }

  if (codes !== null) return <RecoveryCodesShown codes={codes} onDone={onDone} />

  return (
    <main>
      <form className="login card" style={{ maxWidth: 420 }} onSubmit={(e) => void submit(e)}>
        <h2>Set up an authenticator app</h2>
        <div className="body">
          <p className="note" style={{ marginTop: 0 }}>
            {forced
              ? 'This account signs in with an authenticator app. '
              : ''}
            Open Microsoft Authenticator, Google Authenticator or any app that does one-time
            codes, add an account, and scan this code.
          </p>
          {setup === null ? (
            <Spinner />
          ) : (
            <>
              <img src={setup.qrDataUrl} alt="QR code for the authenticator app" width={220} height={220} style={{ display: 'block', margin: '0 auto 12px' }} />
              <p className="note">
                Cannot scan? Enter this key by hand instead:
                <br />
                <code style={{ fontSize: 14, userSelect: 'all' }}>{setup.manualKey}</code>
              </p>
            </>
          )}
          <label htmlFor="code">Six-digit code from the app</label>
          <input id="code" type="text" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />
          <button type="submit" disabled={busy || setup === null || code.trim().length < 6}>
            {busy ? 'Checking…' : 'Confirm'}
          </button>
          {error !== null && <p className="error">{error}</p>}
          {!forced && (
            <p className="note">
              <button type="button" className="link-button" onClick={onDone}>Cancel</button>
            </p>
          )}
        </div>
      </form>
    </main>
  )
}

function RecoveryCodesShown({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  return (
    <main>
      <div className="login card" style={{ maxWidth: 420 }}>
        <h2>Authenticator set up</h2>
        <div className="body">
          <p className="note" style={{ marginTop: 0 }}>
            Keep these recovery codes somewhere safe. Each signs you in once if you lose your phone.
            They are shown only now.
          </p>
          <pre style={{ fontSize: 14, lineHeight: 1.7, userSelect: 'all', margin: '0 0 12px' }}>{codes.join('\n')}</pre>
          <button type="button" onClick={onDone}>I have saved them</button>
        </div>
      </div>
    </main>
  )
}

// --- The account's own security page ----------------------------------------------------

export function SecurityScreen({ onSessionChange }: { onSessionChange: () => void }) {
  const [info, setInfo] = useState<SecurityResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [enrolling, setEnrolling] = useState(false)
  const [codes, setCodes] = useState<string[] | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = () => api.security().then(setInfo).catch((e: unknown) => setError(message(e, 'Could not load')))
  useEffect(() => { void load() }, [])

  if (enrolling) {
    return <EnrolTotp forced={false} onDone={() => { setEnrolling(false); void load(); onSessionChange() }} />
  }
  if (codes !== null) return <RecoveryCodesShown codes={codes} onDone={() => setCodes(null)} />
  if (error !== null) return <Card title="Security"><p className="error">{error}</p></Card>
  if (info === null) return <Spinner />


  const ask = (what: string): string | null => {
    const p = window.prompt(`${what}\n\nEnter your password to confirm:`)
    return p === null || p === '' ? null : p
  }

  return (
    <>
      {notice !== null && <div className="banner ok">{notice}</div>}
      <Card title="Two-factor sign-in" subtitle={`Signed in as ${info.username}`}>
        <table>
          <tbody>
            <tr>
              <td style={{ width: 200 }}>After your password</td>
              <td>
                {info.twoFactor === 'totp' && info.totpEnrolled && <Pill tone="ok">authenticator app</Pill>}
                {info.twoFactor === 'totp' && !info.totpEnrolled && <Pill tone="warn">authenticator app — not set up</Pill>}
                {info.twoFactor === 'email' && (info.mailEnabled
                  ? <Pill tone="ok">code by email</Pill>
                  : <Pill tone="warn">code by email — email is not configured on this service</Pill>)}
                {info.twoFactor === 'none' && <Pill tone="neutral">nothing</Pill>}
              </td>
            </tr>
            {info.totpEnrolled && (
              <tr>
                <td>Recovery codes left</td>
                <td>{info.recoveryCodesLeft}</td>
              </tr>
            )}
          </tbody>
        </table>
        <p className="note">
          {info.totpEnrolled ? (
            <>
              <button type="button" className="link-button" onClick={() => {
                const p = ask('New recovery codes replace the ones you have.')
                if (p !== null) api.recoveryCodes(p).then((r) => setCodes(r.codes)).catch((e: unknown) => setNotice(message(e, 'Failed')))
              }}>
                New recovery codes
              </button>
              {' · '}
              <button type="button" className="link-button" onClick={() => {
                const p = ask('Turn the authenticator off.')
                if (p !== null) api.totpDisable(p).then(() => { setNotice('Authenticator turned off.'); void load(); onSessionChange() }).catch((e: unknown) => setNotice(message(e, 'Failed')))
              }}>
                Turn off the authenticator
              </button>
            </>
          ) : (
            <button type="button" className="link-button" onClick={() => setEnrolling(true)}>
              Set up an authenticator app
            </button>
          )}
        </p>
      </Card>
      <ChangePasswordCard />
    </>
  )
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (next !== again) return setMsg('The two new passwords differ')
    setBusy(true)
    setMsg(null)
    try {
      await api.changePassword(current, next)
      setMsg('Password changed.')
      setCurrent(''); setNext(''); setAgain('')
    } catch (err) {
      setMsg(message(err, 'Could not change the password'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Change password">
      <form className="login" style={{ margin: 0, maxWidth: 340 }} onSubmit={(e) => void submit(e)}>
        <label htmlFor="cp-current">Current password</label>
        <input id="cp-current" type="password" value={current} autoComplete="current-password" onChange={(e) => setCurrent(e.target.value)} />
        <label htmlFor="cp-next">New password</label>
        <input id="cp-next" type="password" value={next} autoComplete="new-password" onChange={(e) => setNext(e.target.value)} />
        <label htmlFor="cp-again">New password again</label>
        <input id="cp-again" type="password" value={again} autoComplete="new-password" onChange={(e) => setAgain(e.target.value)} />
        <button type="submit" disabled={busy || next.length < 12 || current === ''}>{busy ? 'Saving…' : 'Change password'}</button>
        {msg !== null && <p className="note">{msg}</p>}
      </form>
    </Card>
  )
}
